import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/shared/components/atoms/ui/button'
import { requestCenterApi, type DataSourceSearchItem } from '@/features/request-center'

/**
 * `<CommentComposer>` — textarea + `@mention` autocomplete.
 *
 * On `@` keypress, opens a popover under the caret that searches
 * `employees.all` via `requestCenterApi.searchDataSource`. Selecting a
 * result inserts the display-name token into the textarea and stores
 * the resolved employee UUID in local `mentions` state. The submit
 * handler receives both the rendered content + the mentions array.
 *
 * Used in two modes:
 *   - composer (default): "Post" button, blank text on submit
 *   - edit-in-place: receives `initialContent` + `initialMentions`,
 *     "Save" button, optional `onCancel` callback
 */

export interface CommentComposerProps {
	onSubmit: (content: string, mentions: string[], isInternal?: boolean) => Promise<unknown>
	onCancel?: () => void
	initialContent?: string
	initialMentions?: string[]
	submitLabel?: string
	isSubmitting?: boolean
	canPostInternal?: boolean
}

export function CommentComposer({
	onSubmit,
	onCancel,
	initialContent = '',
	initialMentions = [],
	submitLabel = 'Post',
	isSubmitting = false,
	canPostInternal = false,
}: CommentComposerProps) {
	const [content, setContent] = useState(initialContent)
	const [mentions, setMentions] = useState<string[]>(initialMentions)
	// Tracks each inserted mention's `(id → display-label)` so we can
	// drop entries whose token has been deleted from the textarea by
	// the time the user submits. We can't trust the autocomplete
	// `results` cache for this — `results` is keyed on the current
	// query and gets cleared when the popover closes.
	const [insertedLabels, setInsertedLabels] = useState<Record<string, string>>({})
	const [isInternal, setIsInternal] = useState(false)
	const [autocompleteState, setAutocompleteState] = useState<{
		open: boolean
		query: string
		anchorOffset: number
	}>({ open: false, query: '', anchorOffset: 0 })
	const [results, setResults] = useState<DataSourceSearchItem[]>([])
	// Index of the currently-highlighted result row. Reset to 0 every
	// time the result set changes so the highlight always lands on a
	// real row (and so ↑/↓ feel snappy). -1 = nothing highlighted (UX
	// when popover is opening but the search hasn't resolved yet).
	const [activeIndex, setActiveIndex] = useState(0)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// ─── Autocomplete search ─────────────────────────────────────────────────

	useEffect(() => {
		if (!autocompleteState.open) {
			setResults([])
			return
		}
		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => {
			// `setTimeout` expects `() => void`. Wrap the async work so the
			// callback returns void and we can still surface the search
			// result via `setResults`.
			void (async () => {
				try {
					// `employees.active` is the seeded data source (see
					// migration 0019). `employees.all` was a Spec 3 typo —
					// it doesn't exist on the dispatcher and 404s here.
					const res = await requestCenterApi.searchDataSource('employees.active', {
						q: autocompleteState.query,
						limit: 8,
					})
					setResults(res.data)
					// Snap the active index back to the first row whenever the
					// result set changes — keeps ↑/↓ navigation predictable.
					setActiveIndex(res.data.length > 0 ? 0 : -1)
				} catch {
					setResults([])
					setActiveIndex(-1)
				}
			})()
		}, 150)
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [autocompleteState.open, autocompleteState.query])

	// ─── Handlers ────────────────────────────────────────────────────────────

	const onTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const next = e.target.value
		setContent(next)

		// Detect `@<query>` immediately preceding the caret. The popover
		// opens while the user is mid-token; closes when they type a space.
		const caret = e.target.selectionStart
		const before = next.slice(0, caret)
		const match = before.match(/@([\w.-]*)$/)
		if (match) {
			const query = match[1] ?? ''
			setAutocompleteState({ open: true, query, anchorOffset: caret - match[0].length })
		} else {
			setAutocompleteState((prev) => (prev.open ? { ...prev, open: false } : prev))
		}
	}

	const insertMention = (item: DataSourceSearchItem) => {
		const { anchorOffset, query } = autocompleteState
		const before = content.slice(0, anchorOffset)
		const after = content.slice(anchorOffset + 1 + query.length) // skip `@<query>`
		const token = `@${item.label}`
		setContent(`${before}${token} ${after}`)
		setMentions((prev) => (prev.includes(item.value) ? prev : [...prev, item.value]))
		setInsertedLabels((prev) => ({ ...prev, [item.value]: item.label }))
		setAutocompleteState({ open: false, query: '', anchorOffset: 0 })

		// Re-focus the textarea, caret right after the inserted token+space.
		const nextCaret = before.length + token.length + 1
		setTimeout(() => {
			const el = textareaRef.current
			if (el) {
				el.focus()
				el.setSelectionRange(nextCaret, nextCaret)
			}
		}, 0)
	}

	/** Manually remove a mention via the chip row's × button. Strips the
	 *  `@<label>` token from the textarea (first occurrence) so the
	 *  resulting content matches the mentions array. */
	const removeMention = (id: string) => {
		const label = insertedLabels[id]
		setMentions((prev) => prev.filter((m) => m !== id))
		setInsertedLabels((prev) => {
			const next = { ...prev }
			delete next[id]
			return next
		})
		if (label) {
			// Strip `@label` from the body, plus an optional trailing space
			// so we don't leave a double-space behind.
			const re = new RegExp(`@${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} ?`)
			setContent((prev) => prev.replace(re, ''))
		}
	}

	/**
	 * Keyboard nav inside the autocomplete popover. ↑/↓ moves the active
	 * row, Enter/Tab inserts the active result, Esc closes the popover.
	 * Stops propagation when the popover is open so the textarea's
	 * default-handler (e.g. Enter = newline) doesn't fire too.
	 */
	const onTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (!autocompleteState.open || results.length === 0) return
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault()
				setActiveIndex((i) => (i + 1) % results.length)
				return
			case 'ArrowUp':
				e.preventDefault()
				setActiveIndex((i) => (i - 1 + results.length) % results.length)
				return
			case 'Enter':
			case 'Tab': {
				const item = results[activeIndex]
				if (!item) return
				e.preventDefault()
				insertMention(item)
				return
			}
			case 'Escape':
				e.preventDefault()
				setAutocompleteState((prev) => ({ ...prev, open: false }))
				return
			default:
				return
		}
	}

	const handleSubmit = async () => {
		const trimmed = content.trim()
		if (!trimmed || isSubmitting) return
		// Drop mentions whose token no longer appears in the content (the
		// user typed `@alice` then deleted it). Backend doesn't care, but
		// the audit-emitter would otherwise fan out a stale notification.
		// `insertedLabels` is the source of truth for the inserted token —
		// `results` is the autocomplete query cache and may be empty by now.
		const surviving = mentions.filter((id) => {
			const label = insertedLabels[id]
			return !label || trimmed.includes(`@${label}`)
		})
		// Only forward `isInternal` when the admin actually toggled it.
		// Non-admins can't even see the checkbox, so sending `false` from
		// them is dead weight — and the audit emitter treats absent =
		// false anyway.
		const isInternalArg = canPostInternal && isInternal ? true : undefined
		await onSubmit(trimmed, surviving, isInternalArg)
		// On a fresh post (no `initialContent`), clear the field for the
		// next message. On edit, the parent handles unmounting after
		// onSubmit resolves so we don't reset state here.
		if (!initialContent) {
			setContent('')
			setMentions([])
			setInsertedLabels({})
			setIsInternal(false)
		}
	}

	const submitDisabled = useMemo(() => content.trim().length === 0 || isSubmitting, [content, isSubmitting])

	return (
		<div className="space-y-2" data-testid="comment-composer">
			<div className="relative">
				<textarea
					ref={textareaRef}
					value={content}
					onChange={onTextareaChange}
					onKeyDown={onTextareaKeyDown}
					placeholder="Write a comment… use @ to mention an employee"
					rows={3}
					role="combobox"
					aria-expanded={autocompleteState.open}
					aria-controls="mention-popover-listbox"
					aria-activedescendant={
						autocompleteState.open && results[activeIndex] ? `mention-option-${results[activeIndex].value}` : undefined
					}
					className="block w-full rounded border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					data-testid="comment-input"
				/>
				{autocompleteState.open && results.length > 0 ? (
					<ul
						id="mention-popover-listbox"
						role="listbox"
						className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-input bg-background shadow-lg"
						data-testid="mention-popover"
					>
						{results.map((item, idx) => {
							const isActive = idx === activeIndex
							return (
								<li key={item.value} role="option" id={`mention-option-${item.value}`} aria-selected={isActive}>
									<button
										type="button"
										onClick={() => insertMention(item)}
										// Hover events update the active index too — keeps
										// keyboard + mouse in sync (mousing over a row makes
										// it the new "selected" target for Enter).
										onMouseEnter={() => setActiveIndex(idx)}
										data-active={isActive ? 'true' : undefined}
										className={`block w-full px-3 py-2 text-left text-sm focus:outline-none ${
											isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent'
										}`}
									>
										<div className="font-medium">{item.label}</div>
										{item.meta?.email ? <div className="text-xs text-muted-foreground">{item.meta.email}</div> : null}
									</button>
								</li>
							)
						})}
					</ul>
				) : null}
			</div>

			{/* Mention chips — visual cue for every employee currently in the
			    `mentions[]` array. Removing a chip strips the matching token
			    from the textarea so the body and the mentions stay in sync. */}
			{mentions.length > 0 ? (
				<ul className="flex flex-wrap gap-1" data-testid="mention-chips">
					{mentions.map((id) => {
						const label = insertedLabels[id] ?? id
						return (
							<li key={id}>
								<button
									type="button"
									onClick={() => removeMention(id)}
									title="Remove mention"
									className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
									data-testid="mention-chip"
								>
									<span>@{label}</span>
									<span aria-hidden className="text-primary/60">
										×
									</span>
								</button>
							</li>
						)
					})}
				</ul>
			) : null}

			<div className="flex flex-wrap items-center gap-2">
				{canPostInternal ? (
					<label className="flex items-center gap-2 text-xs text-muted-foreground">
						<input
							type="checkbox"
							checked={isInternal}
							onChange={(e) => setIsInternal(e.target.checked)}
							data-testid="comment-internal-toggle"
						/>
						Internal-only
					</label>
				) : null}
				<div className="ml-auto flex gap-2">
					{onCancel ? (
						<Button variant="outline" type="button" onClick={onCancel}>
							Cancel
						</Button>
					) : null}
					<Button
						type="button"
						onClick={() => {
							void handleSubmit()
						}}
						disabled={submitDisabled}
						data-testid="comment-submit"
					>
						{isSubmitting ? 'Saving…' : submitLabel}
					</Button>
				</div>
			</div>
		</div>
	)
}
